import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Copy, Check, AlertTriangle, Info, CalendarRange, Camera } from 'lucide-react';
import { buildProse } from '../../utils/high5WeeklyMailProse';

/**
 * High5 — Haftalık mail taslağı (/high5/haftalik-mail).
 *
 * Mail iki parçadan kuruluyor:
 *  1. **Yazı** — `utils/high5WeeklyMailProse.js` verinin tamamını düz paragrafa
 *     çeviriyor (Efe, 21 Eyl 2026: *"açık açık yazılar istiyorum"*). Sebep/niyet
 *     cümleleri veriden üretilmiyor, elle yazılan paragraflara bırakılıyor.
 *  2. **Panelin kendi ekran görüntüleri** — `/api/high5-weekly-mail/shots`
 *     headless tarayıcıyla panel sayfalarını açıp PNG basıyor
 *     (*"panelden alınma ekran görüntüleri olsun"*). Taklit tablo değil,
 *     `/high5/hedef-takip`, `/high5/haftalik-kpi`, `/high5/acik-artirma`,
 *     `/high5/marka-kapsama` sayfalarının kendisi.
 *
 * "Tümünü kopyala" zengin metin (text/html) yazıyor → Gmail'e yapıştırınca
 * paragraflar ve görseller geliyor. Görseller uzak URL; Gmail yapıştırırken
 * indirip mesaja gömüyor.
 *
 * 🔴 Sidebar'a EKLENMEDİ (*"side bar a falan ekleme ama"*). Rota App.js'te.
 *
 * 🔴🔴 Ciro tanımı: GA4 BigQuery export'u — `/high5/hedef-takip` ile aynı kaynak.
 * Müşterinin kendi tablosu ~%5 yukarıda okuyor (37. hafta: 3.177.578 ₺ ↔
 * GA4 3.016.116 ₺). Üstteki kutuya müşterinin rakamı girilirse giriş
 * paragrafı o rakamla yeniden yazılır, tablolar GA4'te kalır.
 */

const API_BASE = 'https://api.glowytics.com';
const HIGH5_BRAND_ID = 'b20e13f1-2e3f-4c24-83ce-f2b812bfc3e8';
const LS_KEY = 'high5_weekly_mail_notes_v1';

/* ── biçimleyiciler ─────────────────────────────────────────────────────────── */
const nf = (n, d = 0) => (n == null || Number.isNaN(Number(n)) ? '—'
  : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d }));
const money = (n, d = 0) => (n == null ? '—' : `${nf(n, d)} ₺`);
const pct = (n, d = 1) => (n == null ? '—' : `%${nf(n, d)}`);
const signed = (n, f = nf) => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${f(Math.abs(n))}`);
const signedPct = (n) => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}%${nf(Math.abs(n), 1)}`);
const dm = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : '');
const dmy = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '');

/* ── e-postaya yapıştırılabilir stiller (hepsi satır içi) ───────────────────── */
const S = {
  wrap: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '14px', color: '#1f2937', lineHeight: 1.65 },
  h2: { fontSize: '16px', fontWeight: 700, color: '#111827', margin: '30px 0 10px' },
  p: { margin: '0 0 12px' },
  img: { display: 'block', width: '100%', maxWidth: '760px', height: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', margin: '4px 0 6px' },
  cap: { fontSize: '11px', color: '#6b7280', margin: '0 0 18px' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: '12px', margin: '0 0 6px' },
  th: { border: '1px solid #d1d5db', background: '#f3f4f6', padding: '5px 7px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' },
  thL: { border: '1px solid #d1d5db', background: '#f3f4f6', padding: '5px 7px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { border: '1px solid #e5e7eb', padding: '4px 7px', textAlign: 'right', whiteSpace: 'nowrap' },
  tdL: { border: '1px solid #e5e7eb', padding: '4px 7px', textAlign: 'left', whiteSpace: 'nowrap' },
  foot: { fontSize: '11px', color: '#6b7280', margin: '0 0 16px' },
};

function Chg({ value, invert }) {
  if (value == null) return <td style={S.td}>—</td>;
  const good = invert ? value < 0 : value > 0;
  const color = Math.abs(value) < 0.5 ? '#6b7280' : good ? '#15803d' : '#b91c1c';
  return <td style={{ ...S.td, color, fontWeight: 700 }}>{signedPct(value)}</td>;
}

/**
 * Tıklayıp düzeltilen yorum paragrafı — kararı/niyeti anlatan cümleler.
 * 🔑 Boşken `data-empty` taşır; kopyalarken bu paragraflar atılır, yoksa maile
 * "Harcama yorumu — …" gibi ipucu satırları yapışır.
 */
function Note({ id, notes, setNote, placeholder }) {
  const text = notes[id] || '';
  return (
    <p
      style={{ ...S.p, outline: 'none', minHeight: '1.65em', borderLeft: text ? 'none' : '2px dashed #cbd5e1', paddingLeft: text ? 0 : '8px', color: text ? '#1f2937' : '#94a3b8' }}
      contentEditable
      suppressContentEditableWarning
      data-empty={text ? undefined : '1'}
      onBlur={(e) => setNote(id, e.currentTarget.textContent.trim())}
    >
      {text || placeholder}
    </p>
  );
}

const NOT_IPUCU = {
  acilis: 'Açılış cümlesi — haftayı kendi cümlenle özetle.',
  trafik: 'Trafik yorumu — RTB tarafında ne yaptık, neyi bilerek kapattık.',
  harcama: 'Harcama yorumu — bütçeyi neden artırdık, CPC\'ye neden müdahale etmiyoruz.',
  markalar: 'Marka yorumu — hangi markaya kaydırdık, bu hafta nereye yükleneceğiz.',
  kanallar: 'Kanal yorumu — varsa eklemek istediğin not.',
  organik: 'Organik yorumu — içerik/SEO tarafında ne yapıyoruz.',
  marka_kampanya: 'Marka kampanyası yorumu — TBM limitini gevşetecek miyiz.',
  markalar_net: 'Net satış yorumu — rekor kıran marka, ayrıca mail atılacak konular.',
  kapanis: 'Kapanış — toplantı notu, bu hafta ayrıca konuşulacaklar.',
};

export default function High5WeeklyMail() {
  const { selectedBrand, user } = useAuth();
  const [data, setData] = useState(null);
  const [week, setWeek] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [clientRevenue, setClientRevenue] = useState('');
  const [showTables, setShowTables] = useState(false);
  const [notes, setNotes] = useState({});
  const [shots, setShots] = useState(null);
  const [shotsLoading, setShotsLoading] = useState(false);
  const [shotsError, setShotsError] = useState('');
  const mailRef = useRef(null);

  useEffect(() => {
    try { setNotes(JSON.parse(localStorage.getItem(LS_KEY) || '{}')); } catch { /* boş başla */ }
  }, []);
  const setNote = useCallback((id, value) => {
    setNotes((prev) => {
      const next = { ...prev, [id]: value };
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* kota */ }
      return next;
    });
  }, []);

  const brandId = selectedBrand?.id || HIGH5_BRAND_ID;
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', 'X-Brand-ID': brandId }), [brandId]);

  const fetchData = useCallback(async (w) => {
    try {
      setLoading(true); setError('');
      const res = await fetch(`${API_BASE}/api/high5-weekly-mail${w ? `?week=${w}` : ''}`, { credentials: 'include', headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setData(j); setWeek(j.week.start);
    } catch (e) {
      setData(null); setError(e?.message || 'Veri alınamadı');
    } finally { setLoading(false); }
  }, [headers]);

  /** Panel ekran görüntüleri — ilk çekim ~1,5 dk sürer, sonrası önbellekten. */
  const fetchShots = useCallback(async (w, force) => {
    try {
      setShotsLoading(true); setShotsError('');
      const qs = `?week=${w}${force ? '&force=1' : ''}`;
      const res = await fetch(`${API_BASE}/api/high5-weekly-mail/shots${qs}`, { credentials: 'include', headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setShots(j.shots || []);
    } catch (e) {
      setShots(null); setShotsError(e?.message || 'Ekran görüntüleri alınamadı');
    } finally { setShotsLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shotByName = useMemo(() => {
    const m = new Map();
    for (const s of shots || []) if (s.url) m.set(s.ad, s);
    return m;
  }, [shots]);

  const copyAll = async () => {
    if (!mailRef.current) return;
    const clone = mailRef.current.cloneNode(true);
    clone.querySelectorAll('[data-empty="1"]').forEach((n) => n.remove());
    clone.querySelectorAll('[contenteditable]').forEach((n) => n.removeAttribute('contenteditable'));
    clone.querySelectorAll('[data-ui-only]').forEach((n) => n.remove());
    const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1f2937">${clone.innerHTML}</div>`;
    // Düz metin sürümünde görsellerin yerine açıklamaları kalsın.
    clone.querySelectorAll('img').forEach((img) => {
      const t = document.createTextNode(`[ekran görüntüsü: ${img.getAttribute('alt') || ''}]`);
      img.replaceWith(t);
    });
    // innerText satır sonlarını ancak SAYFADA olan düğümde verir.
    clone.style.position = 'fixed'; clone.style.left = '-99999px';
    document.body.appendChild(clone);
    const text = clone.innerText;
    document.body.removeChild(clone);
    try {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      })]);
    } catch {
      await navigator.clipboard.writeText(text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const weekOptions = useMemo(() => {
    if (!data) return [];
    const out = [];
    let d = new Date(`${data.week.start}T00:00:00Z`);
    for (let i = 0; i < 12; i += 1) {
      const s = d.toISOString().slice(0, 10);
      const e = new Date(d); e.setUTCDate(e.getUTCDate() + 6);
      out.push({ value: s, label: `${dm(s)} – ${dm(e.toISOString().slice(0, 10))}` });
      d = new Date(d); d.setUTCDate(d.getUTCDate() - 7);
    }
    return out;
  }, [data]);

  const client = Number(String(clientRevenue).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.'));
  const hasClient = Number.isFinite(client) && client > 0;
  const sections = useMemo(
    () => buildProse(data, { musteriCirosu: hasClient ? client : null }),
    [data, hasClient, client],
  );

  if (error) {
    return <div className="p-6"><div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div></div>;
  }
  if (!data) return <div className="p-6 text-sm text-gray-500">{loading ? 'Yükleniyor…' : 'Veri yok'}</div>;

  const d = data;
  const scStale = d.organic.searchConsole.complete === false;

  /* ── blok başına tablo (isteğe bağlı) ───────────────────────────────────── */
  const TABLES = {
    hafta: (
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.thL}>Tarih</th><th style={S.thL}>Gün</th>
            <th style={S.th}>Hedef ciro</th><th style={S.th}>Gerçekleşen</th><th style={S.th}>HGO</th>
            <th style={S.th}>Hedef TRX</th><th style={S.th}>Gerç.</th>
            <th style={S.th}>Oturum</th>
          </tr>
        </thead>
        <tbody>
          {d.days.map((r) => (
            <tr key={r.date}>
              <td style={S.tdL}>{dm(r.date)}</td>
              <td style={S.tdL}>{r.dowLabel}</td>
              <td style={S.td}>{nf(r.target?.revenue)}</td>
              <td style={S.td}>{nf(r.actual?.revenue)}</td>
              <td style={{ ...S.td, fontWeight: 700, color: (r.hgo?.revenue || 0) >= 100 ? '#15803d' : '#b91c1c' }}>{pct(r.hgo?.revenue, 0)}</td>
              <td style={S.td}>{nf(r.target?.transactions)}</td>
              <td style={S.td}>{nf(r.actual?.transactions)}</td>
              <td style={S.td}>{nf(r.actual?.sessions)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...S.tdL, fontWeight: 700, background: '#f9fafb' }} colSpan={2}>Hafta</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{nf(d.revenue.target)}</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{nf(d.revenue.actual)}</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{pct(d.revenue.hgo, 0)}</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{nf(d.revenue.transactions.target)}</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{nf(d.revenue.transactions.cur)}</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{nf(d.revenue.sessions.cur)}</td>
          </tr>
        </tbody>
      </table>
    ),
    harcama: (
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.thL}>Platform</th>
            <th style={S.th}>Maliyet</th><th style={S.th}>Önceki</th><th style={S.th}>Fark</th>
            <th style={S.th}>Tık</th><th style={S.th}>TBM</th>
          </tr>
        </thead>
        <tbody>
          {d.spend.byPlatform.map((r) => (
            <tr key={r.platform}>
              <td style={S.tdL}>{r.platform}</td>
              <td style={S.td}>{money(r.cost.cur)}</td>
              <td style={S.td}>{money(r.cost.prev)}</td>
              <td style={S.td}>{signed(r.cost.diff, (v) => money(v))}</td>
              <td style={S.td}>{nf(r.clicks.cur)}</td>
              <td style={S.td}>{money(r.cpc.cur, 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ),
    markalar: (
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.thL}>Marka</th>
            <th style={S.th}>Toplam</th><th style={S.th}>Fark</th>
            <th style={S.th}>Google</th><th style={S.th}>Tık</th><th style={S.th}>Fark</th>
            <th style={S.th}>TBM</th><th style={S.th}>Arama IS</th>
            <th style={S.th}>Meta</th>
          </tr>
        </thead>
        <tbody>
          {d.spend.byBrand.slice(0, 10).map((r) => (
            <tr key={r.brand}>
              <td style={S.tdL}>{r.brand}</td>
              <td style={S.td}>{money(r.cost.cur)}</td>
              <td style={S.td}>{signed(r.cost.diff, (v) => money(v))}</td>
              <td style={S.td}>{money(r.google.cost.cur)}</td>
              <td style={S.td}>{nf(r.google.clicks.cur)}</td>
              <td style={S.td}>{signed(r.google.clicks.diff)}</td>
              <td style={S.td}>{money(r.google.cpc.cur, 2)}</td>
              <td style={{ ...S.td, fontWeight: 700 }}>{pct(r.impressionShare.cur)}</td>
              <td style={S.td}>{money(r.meta.cost.cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ),
    kanallar: (
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.thL}>Kanal</th><th style={S.thL}>Tür</th>
            <th style={S.th}>Oturum</th><th style={S.th}>Değişim</th>
            <th style={S.th}>İşlem</th><th style={S.th}>Ciro</th><th style={S.th}>Değişim</th>
          </tr>
        </thead>
        <tbody>
          {d.channels.map((r) => (
            <tr key={r.channel}>
              <td style={S.tdL}>{r.channel}</td>
              <td style={{ ...S.tdL, color: r.family === 'Paid' ? '#1d4ed8' : '#15803d' }}>{r.family}</td>
              <td style={S.td}>{nf(r.sessions.cur)}</td>
              <Chg value={r.sessions.chg} />
              <td style={S.td}>{nf(r.transactions.cur)}</td>
              <td style={S.td}>{money(r.revenue.cur)}</td>
              <Chg value={r.revenue.chg} />
            </tr>
          ))}
        </tbody>
      </table>
    ),
    'net-satis': d.netSales.available ? (
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.thL}>Marka</th>
            <th style={S.th}>Net satış</th><th style={S.th}>Önceki</th>
            <th style={S.th}>Fark</th><th style={S.th}>Değişim</th>
            <th style={S.th}>Adet</th><th style={S.th}>Pay</th>
          </tr>
        </thead>
        <tbody>
          {d.netSales.brands.filter((r) => r.amount.cur !== 0 || r.amount.prev !== 0).map((r) => (
            <tr key={r.brand}>
              <td style={S.tdL}>{r.brand}</td>
              <td style={S.td}>{money(r.amount.cur)}</td>
              <td style={S.td}>{money(r.amount.prev)}</td>
              <td style={S.td}>{signed(r.amount.diff, (v) => money(v))}</td>
              <Chg value={r.amount.chg} />
              <td style={S.td}>{nf(r.qty.cur)}</td>
              <td style={S.td}>{pct(r.share)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...S.tdL, fontWeight: 700, background: '#f9fafb' }}>Toplam</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{money(d.netSales.total.cur)}</td>
            <td style={{ ...S.td, fontWeight: 700, background: '#f9fafb' }}>{money(d.netSales.total.prev)}</td>
            <td style={{ ...S.td, background: '#f9fafb' }} colSpan={4} />
          </tr>
        </tbody>
      </table>
    ) : null,
  };

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      {/* ── araç çubuğu (maile GİRMEZ) ───────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <CalendarRange size={16} className="text-gray-400" /> High5 haftalık mail taslağı
          </div>
          <select
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            value={week}
            onChange={(e) => { setWeek(e.target.value); setShots(null); fetchData(e.target.value); }}
          >
            {weekOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button onClick={() => fetchData(week)} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Veriyi yenile
          </button>
          <button
            onClick={() => fetchShots(week, !!shots)}
            disabled={shotsLoading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
          >
            <Camera size={14} className={shotsLoading ? 'animate-pulse' : ''} />
            {shotsLoading ? 'Panel ekranları çekiliyor…' : shots ? 'Ekranları yeniden çek' : 'Panel ekranlarını çek'}
          </button>
          <button onClick={copyAll} className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-800">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Kopyalandı' : 'Tümünü kopyala'}
          </button>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={showTables} onChange={(e) => setShowTables(e.target.checked)} />
            Tabloları da ekle
          </label>
          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <span>Müşteri tablosundaki ciro:</span>
            <input
              value={clientRevenue}
              onChange={(e) => setClientRevenue(e.target.value)}
              placeholder="ör. 3.177.000"
              className="w-32 rounded-lg border border-gray-300 px-2 py-1 text-right text-xs"
            />
          </div>
        </div>

        <div className="mt-3 grid gap-2 text-[11px] text-gray-600 md:grid-cols-2">
          <div className="flex items-start gap-1.5">
            <Info size={13} className="mt-0.5 shrink-0 text-gray-400" />
            <span>
              Ciro/oturum <b>GA4 BigQuery export'u</b> (<code>/high5/hedef-takip</code> ile aynı kaynak).
              Müşterinin kendi tablosu bu haftalarda ~%5 yukarıda okuyor — kutuya müşterinin rakamını
              yazarsan <b>giriş paragrafı</b> o rakamla yeniden yazılır.
            </span>
          </div>
          <div className="flex items-start gap-1.5">
            <Info size={13} className="mt-0.5 shrink-0 text-gray-400" />
            <span>
              Son veri günü — GA4: <b>{dmy(d.freshness.bqGa4)}</b> · AEM: <b>{dmy(d.freshness.aem)}</b> ·
              Ads IS: <b>{dmy(d.freshness.googleAdsIs)}</b> · Search Console: <b>{dmy(d.freshness.searchConsole)}</b> ·
              ERP haftası: <b>{dmy(d.freshness.erpWeek)}</b>
            </span>
          </div>
        </div>

        {scStale && (
          <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              Search Console verisi hafta sonuna kadar gelmemiş (son gün {dmy(d.organic.searchConsole.lastDateInWeek)}).
              Organik tıklar <b>eksik</b> — mailin organik paragrafında da bu uyarı var.
            </span>
          </div>
        )}
        {shotsError && (
          <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 p-2 text-[11px] text-red-700">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" /> <span>{shotsError}</span>
          </div>
        )}
        {shots && shots.some((s) => s.hata) && (
          <div className="mt-2 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800">
            Çekilemeyen ekran(lar): {shots.filter((s) => s.hata).map((s) => `${s.ad} (${s.hata})`).join(' · ')}
          </div>
        )}
        <div className="mt-2 text-[11px] text-gray-400">
          Gri kesik çizgili paragrafların üstüne tıklayıp kendi yorumunu yaz; tarayıcıda saklanır, kopyalamaya dahil olur.
          Ekran görüntüleri panelin kendi sayfalarından alınır ve hafta bazında önbelleğe yazılır (ilk çekim ~1,5 dk).
          {user?.email ? ` · ${user.email}` : ''}
        </div>
      </div>

      {/* ── kopyalanacak mail gövdesi ─────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8">
        <div ref={mailRef} style={S.wrap}>
          <p style={S.p}>Herkese Selamlar,</p>

          {sections.map((sec) => {
            const shot = sec.shot ? shotByName.get(sec.shot) : null;
            return (
              <React.Fragment key={sec.id}>
                <div style={S.h2}>{sec.baslik}</div>
                {sec.paragraflar.map((t, i) => <p key={i} style={S.p}>{t}</p>)}
                {showTables && TABLES[sec.id]}
                {shot && (
                  <>
                    <img style={S.img} src={shot.url} alt={shot.baslik} />
                    <p style={S.cap}>
                      Panel ekranı: <b>{shot.baslik}</b> — platform.glowytics.com{shot.yol}
                    </p>
                  </>
                )}
                {!shot && sec.shot && !shots && (
                  <p data-ui-only="1" style={{ ...S.cap, color: '#94a3b8' }}>
                    (bu bloğun panel ekranı için yukarıdaki “Panel ekranlarını çek” düğmesine bas)
                  </p>
                )}
                {sec.notYeri && (
                  <Note id={sec.notYeri} notes={notes} setNote={setNote} placeholder={NOT_IPUCU[sec.notYeri] || 'Yorum'} />
                )}
              </React.Fragment>
            );
          })}

          <Note id="kapanis" notes={notes} setNote={setNote} placeholder={NOT_IPUCU.kapanis} />
          <p style={S.p}>Sevgiler,</p>
        </div>
      </div>
    </div>
  );
}
